import React, { useMemo, useState } from 'react'
import { ImageOff, FolderOpen, RefreshCw, Check, AlertCircle } from 'lucide-react'
import PhotoCard from './PhotoCard'
import useCategoryStore from '../../stores/categoryStore'
import { groupPhotosByCategory } from '../../utils/helpers'

// Get image URL (relative URLs work via Vite proxy in dev)
function getImageUrl(photo) {
  return photo.thumbnailUrl || photo.imageUrl || photo.thumbnail || photo.imageData || ''
}

export default function PhotoGallery({
  photos,
  onDeletePhoto,
  onUpdatePhotoNotes,
  onTogglePhotoAnalyse,
  onReanalyseCategory,
  analysisResults = null,
  filterCategory = null,
  groupByCategory = true
}) {
  const getActiveCategories = useCategoryStore(state => state.getActiveCategories)
  const customCategories = useCategoryStore(state => state.customCategories)

  // Get all categories including custom (static + custom in sections + standalone)
  const categories = [...getActiveCategories(), ...customCategories.filter(c => !c.section).map(c => ({
    code: c.code,
    name: c.name,
    icon: c.icon,
    section: 'X',
    order: 999, // Sort standalone custom categories last
    isCustom: true
  }))]

  // Filter photos if category filter is provided
  const filteredPhotos = useMemo(() => {
    if (!filterCategory) return photos
    return photos.filter(p => p.categoryCode === filterCategory.code)
  }, [photos, filterCategory])

  // Group photos by category if enabled
  const groupedPhotos = useMemo(() => {
    if (!groupByCategory) return null
    return groupPhotosByCategory(filteredPhotos, categories)
  }, [filteredPhotos, categories, groupByCategory])

  if (filteredPhotos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
        <ImageOff className="w-16 h-16 mb-4" />
        <p className="text-lg font-medium">No photos yet</p>
        <p className="text-sm">
          {filterCategory
            ? `No photos in ${filterCategory.name}`
            : 'Start capturing photos for this survey'
          }
        </p>
      </div>
    )
  }

  // Flat grid view (no grouping)
  if (!groupByCategory) {
    return (
      <div className="photo-grid">
        {filteredPhotos.map((photo) => (
          <PhotoCard
            key={photo.id}
            photo={photo}
            onDelete={onDeletePhoto}
            onUpdateNotes={onUpdatePhotoNotes}
            onToggleAnalyse={onTogglePhotoAnalyse}
            showCategory={!filterCategory}
          />
        ))}
      </div>
    )
  }

  // Grouped view by category
  return (
    <div className="space-y-8">
      {Object.entries(groupedPhotos)
        .filter(([_, data]) => data.photos.length > 0)
        .sort((a, b) => a[1].category.order - b[1].category.order)
        .map(([categoryCode, data]) => {
          const categoryAnalysis = analysisResults?.categories?.[categoryCode]
          const hasAnalysis = categoryAnalysis?.success

          return (
          <div key={categoryCode}>
            {/* Category header */}
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <FolderOpen className="w-5 h-5 text-primary-600" />
                <h3 className="font-semibold text-slate-900">
                  {data.category.name}
                </h3>
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-sm font-medium rounded-full">
                  {data.photos.length} {data.photos.length === 1 ? 'photo' : 'photos'}
                </span>
                {hasAnalysis && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    <Check className="w-3 h-3" />
                    Analysed
                  </span>
                )}
              </div>

              {/* Re-analyse button - only show if we have a callback and photos to analyse */}
              {onReanalyseCategory && data.photos.some(p => p.analyseWithAI !== false) && (
                <ReanalyseButton
                  categoryCode={categoryCode}
                  categoryName={data.category.name}
                  onReanalyse={onReanalyseCategory}
                  hasExistingAnalysis={hasAnalysis}
                />
              )}
            </div>

            {/* Photos grid */}
            <div className="photo-grid">
              {data.photos.map((photo) => (
                <PhotoCard
                  key={photo.id}
                  photo={photo}
                  onDelete={onDeletePhoto}
                  onUpdateNotes={onUpdatePhotoNotes}
                  onToggleAnalyse={onTogglePhotoAnalyse}
                  showCategory={false}
                />
              ))}
            </div>
          </div>
        )})}
    </div>
  )
}

// Re-analyse button component with loading state
function ReanalyseButton({ categoryCode, categoryName, onReanalyse, hasExistingAnalysis }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const handleClick = async () => {
    setLoading(true)
    setError(null)
    setSuccess(false)

    try {
      await onReanalyse(categoryCode)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError(err.message || 'Analysis failed')
      setTimeout(() => setError(null), 5000)
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <span className="flex items-center gap-1 text-sm text-green-600">
        <Check className="w-4 h-4" />
        Updated
      </span>
    )
  }

  if (error) {
    return (
      <span className="flex items-center gap-1 text-sm text-red-600" title={error}>
        <AlertCircle className="w-4 h-4" />
        Failed
      </span>
    )
  }

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className="flex items-center gap-1 px-2 py-1 text-sm text-slate-600 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors disabled:opacity-50"
      title={hasExistingAnalysis ? 'Re-analyse this category' : 'Analyse this category'}
    >
      <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
      {loading ? 'Analysing...' : hasExistingAnalysis ? 'Re-analyse' : 'Analyse'}
    </button>
  )
}

// Compact gallery for survey card preview
export function PhotoGalleryPreview({ photos, maxDisplay = 4 }) {
  if (photos.length === 0) {
    return (
      <div className="flex items-center justify-center h-20 bg-slate-50 rounded-lg text-slate-400 text-sm">
        No photos
      </div>
    )
  }

  const displayPhotos = photos.slice(0, maxDisplay)
  const remaining = photos.length - maxDisplay

  return (
    <div className="flex gap-1">
      {displayPhotos.map((photo) => (
        <div
          key={photo.id}
          className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100"
        >
          <img
            src={getImageUrl(photo)}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      ))}
      {remaining > 0 && (
        <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 font-medium">
          +{remaining}
        </div>
      )}
    </div>
  )
}
